import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useLogin } from 'services/auth/hook';
import useAuthenticate from './useAuthenticate';
import {
  LoginOTPResponse,
  LoginResponse,
  OTPChannel,
} from 'services/auth/type';
import {
  login,
  resendSignUpOTP,
  sendSignUpOTP,
  verifySignUpOTP,
} from 'services/auth/api';
import useAuthStore from 'store/auth';
import { PATH_ROUTES } from 'routes/path';
import { AxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';

export default function useOTPLogin(channel: 'phone' | 'email') {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleLoginResponse, setUserDetails } = useAuthenticate();

  const setSignupEmail = useAuthStore((store) => store.setSignupEmail);
  const setSignupPhone = useAuthStore((store) => store.setSignupPhone);
  const setSignupUserId = useAuthStore((store) => store.setSignupUserId);
  const setEmailVerified = useAuthStore((store) => store.setEmailVerified);
  const setPhoneVerified = useAuthStore((store) => store.setPhoneVerified);

  const [isOtpSent, setIsOtpSent] = useState<boolean>(false);
  const [session, setSession] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>('');
  const [isResending, setIsResending] = useState<boolean>(false);
  const [isNewUser, setIsNewUser] = useState(false);

  const { mutate, data: loginOTPResponse } = useLogin({
    onSuccess: async (data, variables) => {
      console.log('🚀 ~ useOTPLogin ~ data:', data);
      if (!isOtpSent) {
        const { otp_sent, session, user_name } =
          data as unknown as LoginOTPResponse;
        setIsLoading(false);

        if (
          ('phone_verification_flag' in data &&
            !data?.phone_verification_flag) ||
          ('email_verification_flag' in data && !data?.email_verification_flag)
        ) {
          toast.error(t('verify_details_msg'));
          setSignupEmail(data?.user_name || '');
          setSignupPhone(data?.phone_number || '');
          setSignupUserId(data?.user_id || '');
          setEmailVerified(data?.email_verification_flag || false);
          setPhoneVerified(data?.phone_verification_flag || false);
          navigate(PATH_ROUTES.SIGNUP_VERIFICATION);
          return;
        }

        if (otp_sent) {
          setSession(session);
          setUserName(user_name);
          setIsOtpSent(true);
          toast.success(
            `${t('otp_sent_msg')} ${
              channel === 'email' ? t('email_address') : t('whatsapp_number')
            }`
          );
        }
      } else {
        const res = data as unknown as LoginOTPResponse & LoginResponse;
        if ('user_password' in res && res.user_password === false) {
          return navigate(PATH_ROUTES.OTP_REGISTER, {
            state:
              channel === 'email'
                ? { email: variables.username }
                : { phoneNumber: variables.username, session: session || '' },
          });
        }

        if (res?.session) {
          setSession(res.session);
          toast.error(t('invalid_otp_msg'));
          setIsLoading(false);
          return;
        }

        try {
          await handleLoginResponse({
            res,
          });
        } catch (error) {
          console.error(error);
          setIsLoading(false);
          return;
        }

        setUserDetails({
          email: userName,
          setLoading: setIsLoading,
          isLogin: true,
        });
      }
    },
    onError: async (error, variables) => {
      if (error instanceof AxiosError) {
        const res = error.response;
        if (res?.data?.new_user) {
          try {
            const res = await sendSignUpOTP({
              username: variables.username,
              ...(variables.channel && { channel: variables.channel }),
            });
            console.log('🚀 ~ OTPLoginScreen ~ res:', res);

            if (res?.error) {
              return toast.error(res?.error);
            }
            setIsNewUser(true);
            if (res?.otp_sent) {
              setUserName(res.user_name || '');
              setIsOtpSent(true);
              setSession(res?.session || '');
              toast.success(
                `${t('otp_sent_msg')} ${
                  channel === 'email'
                    ? t('email_address')
                    : t('whatsapp_number')
                }`
              );
            }
          } catch (error) {
            return toast.error(res?.data?.error);
          }
        }
      }
      setIsLoading(false);
    },
  });
  const { mutate: mutateSignUp } = useMutation({
    mutationKey: ['verify-signup-otp'],
    mutationFn: verifySignUpOTP,
    onSuccess: (data, variables) => {
      console.log('🚀 ~ useOTPLogin ~ data:', data);
      setIsLoading(false);

      if (
        loginOTPResponse &&
        'phone_verification_flag' in loginOTPResponse &&
        'email_verification_flag' in loginOTPResponse &&
        loginOTPResponse?.phone_verification_flag === true &&
        loginOTPResponse?.email_verification_flag === false
      ) {
        toast.error(t('verify_details_msg'));
        setSignupEmail(loginOTPResponse?.user_name || '');
        setSignupPhone(loginOTPResponse?.phone_number || '');
        setSignupUserId(loginOTPResponse?.user_id || '');
        setEmailVerified(loginOTPResponse?.email_verification_flag || false);
        setPhoneVerified(loginOTPResponse?.phone_verification_flag || false);
        navigate(PATH_ROUTES.SIGNUP_VERIFICATION, {
          state: { isOTPSignup: true },
        });
        return;
      }

      navigate(PATH_ROUTES.OTP_REGISTER, {
        state:
          channel === 'email'
            ? { email: variables.username }
            : { phoneNumber: variables.username, session: session || '' },
      });
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  const resendOTP = async (
    userName: string,
    selectedOTPChannel?: OTPChannel
  ) => {
    try {
      setIsResending(true);
      const res = await login({
        username: userName,
        auth_type: 'otp',
        ...(channel && { channel: selectedOTPChannel }),
      });
      const session = (res as unknown as LoginOTPResponse)?.session;
      if (!session) {
        toast.error(t('failed_to_send_otp_msg'));
        return;
      }

      setSession(session);
      toast.success(
        `${t('otp_resent_msg')} ${
          channel === 'email' ? t('email_address') : t('whatsapp_number')
        }`
      );
    } catch (error) {
      toast.error(t('failed_to_send_otp_msg'));
      console.error(error);
    } finally {
      setIsResending(false);
    }
  };
  const resendSignup = async (
    userName: string,
    selectedOTPChannel?: OTPChannel
  ) => {
    try {
      setIsResending(true);
      const res = await resendSignUpOTP({
        username: userName,
        ...(channel && { channel: selectedOTPChannel }),
      });
      const session = res?.session || '';
      setSession(session);
      toast.success(
        `${t('otp_resent_msg')} ${
          channel === 'email' ? t('email_address') : t('whatsapp_number')
        }`
      );
    } catch (error) {
      toast.error(t('failed_to_send_otp_msg'));
      console.error(error);
    } finally {
      setIsResending(false);
    }
  };

  return {
    mutate,
    mutateSignUp,
    session,
    isLoading,
    isOtpSent,
    setIsLoading,
    userName,
    setSession,
    resendOTP,
    isResending,
    isNewUser,
    setIsOtpSent,
    resendSignupOtp: resendSignup,
  };
}
